import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Drawer, Input, Modal, Row, Select, Space, Switch, Table, Tooltip, Typography } from 'antd';
import { EyeOutlined, SearchOutlined } from '@ant-design/icons';
import { debounce } from 'lodash';
import mjml2html from 'mjml-browser';
import PenIcon from 'assets/icons/pen.svg';
import { MailingTemplate } from '../../services/MailingService';

const DEFAULT_PAGE_SIZE = 10;
type TemplateStatusFilter = 'ACTIVE' | 'PASSIVE';

export interface MailingTemplatesTabProps {
  templates: MailingTemplate[];
  loading: boolean;
  updatingTemplateStatusId: string | null;
  onOpenTemplateModal: (template?: MailingTemplate) => void;
  onToggleTemplateStatus: (templateId: string, active: boolean) => Promise<void>;
}

const normalizeText = (value?: string | null) => (value || '').trim().toLocaleLowerCase('tr-TR');
const applyTemplatePreviewData = (content: string) => {
  if (!content) return '';
  return content
    .replaceAll('[firstName]', 'Ada')
    .replaceAll('[lastName]', 'Lovelace')
    .replaceAll('[email]', 'ada@example.com')
    .replaceAll('[phone]', '+90 555 123 45 67')
    .replaceAll('[companyName]', 'Analytical Engines')
    .replaceAll('[id]', '9f56b469-e433-4cd7-9989-5f8a6d0f6a14')
    .replaceAll('{{first_name}}', 'Ada')
    .replaceAll('{{last_name}}', 'Lovelace')
    .replaceAll('{{email}}', 'ada@example.com')
    .replaceAll('{{company_name}}', 'Analytical Engines')
    .replaceAll('[surveyLink]', 'https://app.example.com/public/mailing-survey?token=sample')
    .replaceAll('[surveyName]', 'Müşteri Memnuniyet Anketi')
    .replaceAll('[responseDeadline]', '31.12.2026 18:00');
};

const MailingTemplatesTab: React.FC<MailingTemplatesTabProps> = ({
  templates,
  loading,
  updatingTemplateStatusId,
  onOpenTemplateModal,
  onToggleTemplateStatus,
}) => {
  const { Text } = Typography;

  const [selectedStatusFilter, setSelectedStatusFilter] = useState<TemplateStatusFilter>('ACTIVE');
  const [pendingStatusFilter, setPendingStatusFilter] = useState<TemplateStatusFilter>('ACTIVE');
  const [filterOpen, setFilterOpen] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState<MailingTemplate | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const debouncedApplySearch = React.useMemo(
    () =>
      debounce((value: string) => {
        setSearch(value);
      }, 300),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedApplySearch.cancel();
    };
  }, [debouncedApplySearch]);

  const filteredTemplates = useMemo(() => {
    const normalizedSearch = normalizeText(search);

    return templates
      .filter((template) => {
        if (selectedStatusFilter === 'ACTIVE') {
          return template.active !== false;
        }
        return template.active === false;
      })
      .filter((template) => {
        if (!normalizedSearch) return true;
        const searchable = [template.name, template.subject, template.content].map((value) => normalizeText(value)).join(' ');
        return searchable.includes(normalizedSearch);
      });
  }, [search, selectedStatusFilter, templates]);

  useEffect(() => {
    setPage(1);
  }, [selectedStatusFilter, search]);

  const openFilterDrawer = () => {
    setPendingStatusFilter(selectedStatusFilter);
    setFilterOpen(true);
  };

  const clearPendingFilters = () => {
    setPendingStatusFilter('ACTIVE');
  };

  const applyPendingFilters = () => {
    setSelectedStatusFilter(pendingStatusFilter);
    setPage(1);
    setFilterOpen(false);
  };

  const total = filteredTemplates.length;
  const previewDoc = useMemo(() => {
    const source = applyTemplatePreviewData(previewTemplate?.content || '');
    if (!source.trim()) {
      return '<!doctype html><html><body style="font-family:Arial,sans-serif;padding:16px;color:#666;">Canlı önizleme burada görünecek.</body></html>';
    }

    if (source.toLowerCase().includes('<mjml')) {
      try {
        const result = mjml2html(source, { validationLevel: 'soft' });
        return result.html || '<html><body>MJML dönüştürülemedi.</body></html>';
      } catch (error) {
        return `<html><body style="font-family:monospace;padding:16px;color:#b00020;">MJML hata: ${String(error)}</body></html>`;
      }
    }
    return source;
  }, [previewTemplate]);

  return (
    <>
      <Card
        className='br-30px kontrolx-card'
        title={
          <Row align='middle'>
            <Text style={{ fontWeight: 600 }}>Şablon Listesi</Text>
            <Text type='secondary' className='ms-5' style={{ fontWeight: 500 }}>
              <strong>({total})</strong> kayıt listelendi
            </Text>
          </Row>
        }
        extra={
          <Space>
            <Input
              allowClear
              placeholder='Ara'
              prefix={<SearchOutlined style={{ color: '#b8b9bc' }} />}
              className='d-input d-input-form d-input-filter'
              style={{ width: 220 }}
              value={searchInput}
              onChange={(event) => {
                const value = event.target.value || '';
                setSearchInput(value);
                if (value.length >= 2) {
                  debouncedApplySearch(value);
                } else if (value.length === 0) {
                  debouncedApplySearch('');
                }
              }}
            />
            <Button type='primary' onClick={openFilterDrawer}>
              Filtrele
            </Button>
            <Button type='primary' onClick={() => onOpenTemplateModal()}>
              Ekle
            </Button>
          </Space>
        }
      >
        <Table<MailingTemplate>
          rowKey='id'
          loading={loading}
          dataSource={filteredTemplates}
          pagination={{
            current: page,
            pageSize,
            total,
            onChange: (nextPage, nextPageSize) => {
              setPage(nextPage);
              setPageSize(nextPageSize || DEFAULT_PAGE_SIZE);
            },
          }}
          columns={[
            { title: 'Ad', dataIndex: 'name', key: 'name' },
            { title: 'Konu', dataIndex: 'subject', key: 'subject' },
            {
              title: 'Durum',
              dataIndex: 'active',
              key: 'active',
              render: (active: boolean | undefined, record) => (
                <Switch
                  checked={active !== false}
                  checkedChildren='AKTIF'
                  unCheckedChildren='PASIF'
                  loading={updatingTemplateStatusId === record.id}
                  onChange={(checked) => onToggleTemplateStatus(record.id, checked)}
                />
              ),
            },
            {
              title: 'İşlemler',
              key: 'actions',
              render: (_: unknown, record) => (
                <Space>
                  <Tooltip title='Ön İzleme'>
                    <Button
                      icon={<EyeOutlined className='icon' />}
                      shape='circle'
                      onClick={() => setPreviewTemplate(record)}
                      className='icon-button'
                    />
                  </Tooltip>
                  <Tooltip title='Düzenle'>
                    <Button
                      icon={<img src={PenIcon} alt='pen' className='icon' />}
                      shape='circle'
                      onClick={() => onOpenTemplateModal(record)}
                      className='icon-button'
                    />
                  </Tooltip>
                </Space>
              ),
            },
          ]}
        />
      </Card>

      <Drawer open={filterOpen} onClose={() => setFilterOpen(false)} destroyOnClose width={420} title='Filtre'>
        <Space direction='vertical' style={{ width: '100%' }} size='large'>
          <div>
            <Typography.Text>Durum</Typography.Text>
            <Select<TemplateStatusFilter>
              style={{ width: '100%', marginTop: 8 }}
              value={pendingStatusFilter}
              onChange={setPendingStatusFilter}
              options={[
                { label: 'Aktif', value: 'ACTIVE' },
                { label: 'Pasif', value: 'PASSIVE' },
              ]}
            />
          </div>

          <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
            <Button onClick={clearPendingFilters}>Temizle</Button>
            <Button type='primary' onClick={applyPendingFilters}>
              Filtrele
            </Button>
          </Space>
        </Space>
      </Drawer>

      <Modal
        title={previewTemplate ? `${previewTemplate.name} - Ön İzleme` : 'Ön İzleme'}
        open={Boolean(previewTemplate)}
        onCancel={() => setPreviewTemplate(null)}
        footer={null}
        width={1100}
        destroyOnClose
      >
        <iframe
          title='mail-template-preview'
          srcDoc={previewDoc}
          style={{ width: '100%', height: 620, border: '1px solid #f0f0f0', borderRadius: 8, background: '#fff' }}
        />
      </Modal>
    </>
  );
};

export default MailingTemplatesTab;
